import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type GameCreated } from '../api';
import { BrandMark, ThemeToggle, type ThemeMode } from '../components/Theme';

const PACKAGE_GROUPS: { category: string; packages: { id: string; label: string }[] }[] = [
  {
    category: 'Player Model',
    packages: [
      { id: 'character', label: 'Character (world avatar)' },
      { id: 'hero', label: 'Hero (collectible roster)' },
    ],
  },
  {
    category: 'Core',
    packages: [
      { id: 'resource', label: 'Resource' },
      { id: 'inventory', label: 'Inventory' },
      { id: 'loot', label: 'Loot' },
      { id: 'progression', label: 'Progression' },
    ],
  },
  {
    category: 'Content',
    packages: [
      { id: 'crafting', label: 'Crafting' },
      { id: 'combat', label: 'Combat' },
      { id: 'shop', label: 'Shop' },
      { id: 'quest', label: 'Quest' },
      { id: 'achievement', label: 'Achievement' },
    ],
  },
  {
    category: 'Social',
    packages: [
      { id: 'social', label: 'Social' },
      { id: 'trading', label: 'Trading' },
    ],
  },
  {
    category: 'World',
    packages: [
      { id: 'world', label: 'World' },
      { id: 'liveops', label: 'LiveOps' },
      { id: 'matchmaking', label: 'Matchmaking' },
      { id: 'leaderboard', label: 'Leaderboard' },
      { id: 'event', label: 'Event' },
    ],
  },
  {
    category: 'Analytics',
    packages: [
      { id: 'analytics', label: 'Analytics' },
    ],
  },
];

const STARTER_PACKAGES = new Set([
  'character',
  'resource',
  'inventory',
  'loot',
  'progression',
  'crafting',
  'combat',
  'shop',
  'quest',
]);

const PACKAGE_HELP: Record<string, string> = {
  resource: 'Currencies, materials, energy, and spendable balances.',
  inventory: 'Items, equipment, grants, removes, and player bags.',
  loot: 'Drop tables, weighted rolls, and reward previews.',
  progression: 'XP tracks, levels, unlocks, and skill trees.',
  crafting: 'Recipes, ingredients, costs, and crafted outputs.',
  combat: 'Stages, battle previews, and combat simulation.',
  shop: 'Stores, listings, purchases, and price checks.',
  quest: 'Quest definitions, objectives, starts, and claims.',
  achievement: 'Achievements, tiers, events, and rewards.',
  social: 'Guilds, mail, joins, leaves, and inbox tools.',
  trading: 'Player listings, buys, cancels, and market summaries.',
  world: 'Zones, travel, nodes, and gathering loops.',
  liveops: 'Seasons, login rewards, and battle pass progress.',
  matchmaking: 'Ratings, opponent search, and bracket generation.',
  leaderboard: 'Boards, score submission, resets, and ranks.',
  event: 'Timed events, progress, damage, starts, and completion.',
  analytics: 'Event recording and operational dashboard metrics.',
};

export default function Onboarding({
  onGameCreated,
  theme,
  onThemeChange,
}: {
  onGameCreated: (game: GameCreated) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
}) {
  const [step, setStep] = useState(1);
  const [gameName, setGameName] = useState('');
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const togglePackage = (id: string) => {
    setSelectedPackages(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    const all = PACKAGE_GROUPS.flatMap(g => g.packages.map(p => p.id));
    setSelectedPackages(new Set(all));
  };

  const selectStarter = () => {
    setSelectedPackages(new Set(STARTER_PACKAGES));
  };

  const selectNone = () => {
    setSelectedPackages(new Set());
  };

  const handleSubmit = async () => {
    if (!gameName.trim()) {
      setError('Game name is required');
      return;
    }
    if (selectedPackages.size === 0) {
      setError('Select at least one package');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const game = await api.createGame(gameName.trim(), Array.from(selectedPackages));
      onGameCreated(game);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4 py-12">
      <div className="fixed right-4 top-4">
        <ThemeToggle value={theme} onChange={onThemeChange} />
      </div>
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 justify-center mb-8">
          <BrandMark size="lg" />
          <div className="text-xl font-semibold tracking-tight">GameForge</div>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`w-8 h-1 rounded-full ${step >= 1 ? 'bg-brand' : 'bg-line'}`} />
          <div className={`w-8 h-1 rounded-full ${step >= 2 ? 'bg-brand' : 'bg-line'}`} />
        </div>

        <div className="card">
          {error && <div className="error-banner mb-4">{error}</div>}

          {step === 1 && (
            <>
              <h2 className="text-lg font-semibold mb-1">Create your game</h2>
              <p className="text-sm text-fg-dim mb-4">
                This creates a workspace for one title. Packages define which game systems show up in the portal.
              </p>
              <div className="mb-4">
                <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                  Game name
                </label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. My Awesome RPG"
                  value={gameName}
                  onChange={e => setGameName(e.target.value)}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter' && gameName.trim()) setStep(2);
                  }}
                />
              </div>
              <button
                className="btn btn-primary w-full"
                disabled={!gameName.trim()}
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-lg font-semibold mb-1">Select packages</h2>
              <p className="text-sm text-fg-dim mb-4">
                Choose the systems you want to configure and test for <strong>{gameName}</strong>.
                You can add more later.
              </p>

              <div className="flex gap-2 mb-4">
                <button className="btn btn-ghost text-xs" onClick={selectStarter}>
                  Starter set
                </button>
                <button className="btn btn-ghost text-xs" onClick={selectAll}>
                  Select all
                </button>
                <button className="btn btn-ghost text-xs" onClick={selectNone}>
                  Clear
                </button>
              </div>

              <div className="flex flex-col gap-4 mb-6 max-h-[400px] overflow-y-auto">
                {PACKAGE_GROUPS.map(group => (
                  <div key={group.category}>
                    <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mb-1.5">
                      {group.category}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.packages.map(pkg => (
                        <button
                          key={pkg.id}
                          className={[
                            'package-option',
                            selectedPackages.has(pkg.id)
                              ? 'package-option-selected'
                              : '',
                          ].join(' ')}
                          onClick={() => togglePackage(pkg.id)}
                        >
                          <span className="font-medium text-fg">{pkg.label}</span>
                          <span className="text-[11.5px] text-fg-dim">{PACKAGE_HELP[pkg.id]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button className="btn btn-ghost flex-1" onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  className="btn btn-primary flex-1"
                  disabled={selectedPackages.size === 0 || loading}
                  onClick={handleSubmit}
                >
                  {loading ? 'Creating...' : `Create game (${selectedPackages.size} packages)`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
